import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import PasswordInput from "@/components/auth/PasswordInput";
import { toast } from "sonner";
import { z } from "zod";
import { Lock, CheckCircle } from "lucide-react";

const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters");

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Check if we have a valid session from the reset link
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error(t("auth.errors.invalidResetLink"));
        navigate("/forgot-password");
      }
    };
    checkSession();
  }, [navigate, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate password
    const result = passwordSchema.safeParse(password);
    if (!result.success) {
      setError(t("auth.errors.passwordTooShort"));
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      setError(t("auth.errors.passwordMismatch"));
      return;
    }

    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({
      password: password,
    });
    setLoading(false);

    if (updateError) {
      toast.error(updateError.message);
    } else {
      setSuccess(true);
      // Sign out and redirect to login after 3 seconds
      // IMPORTANT: Must await signOut to complete before redirecting
      // Use global scope to clear all sessions (including the one from reset link)
      setTimeout(async () => {
        try {
          await supabase.auth.signOut({ scope: 'global' });
        } catch (e) {
          console.error('Sign out error:', e);
        }
        // Use replace to prevent back navigation to reset page
        window.location.replace("/login");
      }, 3000);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-[hsl(var(--accent-cyan))] rounded-2xl border-2 border-foreground p-8 text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4" />
            <h1 className="heading-card mb-4">{t("auth.passwordResetSuccess")}</h1>
            <p className="text-foreground/80 mb-6">{t("auth.passwordResetSuccessDesc")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-[hsl(var(--accent-yellow))] rounded-2xl border-2 border-foreground p-8">
          <div className="text-center mb-8">
            <Lock className="w-12 h-12 mx-auto mb-4" />
            <h1 className="heading-card mb-2">{t("auth.resetPassword")}</h1>
            <p className="text-foreground/80 text-sm">{t("auth.resetPasswordDesc")}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <PasswordInput
              label={t("auth.newPassword")}
              value={password}
              onChange={setPassword}
              placeholder={t("auth.newPasswordPlaceholder")}
              showPasswordExternal={showPassword}
              onShowPasswordChange={setShowPassword}
            />

            <PasswordInput
              label={t("auth.confirmPassword")}
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder={t("auth.confirmPasswordPlaceholder")}
              error={error}
              showPasswordExternal={showPassword}
              onShowPasswordChange={setShowPassword}
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary justify-center disabled:opacity-50"
            >
              {loading ? t("auth.loading") : t("auth.updatePassword")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
